import React, { useEffect } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const CORPORATE_FAQS = [  {
    id: 1,
    question: 'What does a corporate finance advisor do?',
    answer: 'A corporate finance advisor assesses a company\u2019s financial position, structures the appropriate capital solution, evaluates funding alternatives across lenders and investors, negotiates commercial terms and coordinates the transaction through documentation, sanction and financial closure. The objective is to arrange capital that fits the business — rather than fitting the business to a predetermined product.'
  },
  {
    id: 2,
    question: 'How does LOANYARD CAPITAL structure business financing?',
    answer: 'Every mandate begins with the funding purpose, cash flows, existing leverage, collateral, repayment ability and industry dynamics. Based on this assessment, we identify the financing instruments that fit — term debt, working capital, structured credit, asset-backed or trade finance — and then position the requirement with institutions whose credit appetite matches the transaction.'
  },
  {
    id: 3,
    question: 'What is debt syndication?',
    answer: 'Debt syndication is the process of raising a financing requirement from multiple banks, NBFCs or financial institutions under a common structure, instead of depending on a single lender. It solves single-lender exposure limits for larger requirements and creates competitive tension that typically improves pricing, security terms and covenants.'
  },
  {
    id: 4,
    question: 'When should a business consider structured finance?',
    answer: 'Structured finance is relevant when conventional lending does not fully address the requirement — for example, when cash flows are seasonal or back-ended, when security cover is unconventional, when an acquisition or consolidation needs tailored repayment, or when existing debt needs to be reorganised around the business\u2019s actual cash generation.'
  },
  {
    id: 5,
    question: 'How do you select lenders for a financing requirement?',
    answer: 'Institutions are evaluated on live credit appetite, sector preference, comfort with the transaction size, collateral expectations, pricing, tenure, covenant flexibility and execution capability. The requirement is positioned in parallel with shortlisted institutions and term sheets are compared on commercial and structural parameters — not headline rate alone.'
  },
  {
    id: 6,
    question: 'What information is required for an initial funding assessment?',
    answer: 'A business profile, the funding requirement and its purpose, recent financial statements, details of existing debt, cash-flow visibility, collateral or security where relevant, and the transaction objectives. Initial assessments are handled confidentially and typically indicate feasible structures within a few working days.'
  }
];

export { CORPORATE_FAQS };

export const FAQ = () => {
  useEffect(() => {
    if (document.getElementById('home-faq-schema')) return;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: CORPORATE_FAQS.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer }
      }))
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'home-faq-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('home-faq-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Corporate Finance <span className="text-emerald-600">FAQ</span>
            </h2>
            <p className="text-lg text-slate-600">
              Common questions about corporate finance, capital structuring and our advisory process
            </p>
          </div>

          {/* FAQ Accordion */}
          <Accordion type="single" collapsible className="space-y-4">
            {CORPORATE_FAQS.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={`item-${faq.id}`}
                className="bg-slate-50 border-2 border-slate-200 rounded-xl px-6 hover:border-emerald-500 transition-colors"
              >
                <AccordionTrigger className="text-left text-lg font-semibold text-slate-900 hover:text-emerald-600 py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Contact CTA */}
          <div className="text-center mt-12 p-8 bg-emerald-50 rounded-2xl border border-emerald-200">
            <p className="text-lg text-slate-700 mb-4">
              Have a specific requirement to discuss? Our advisory team is available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+919892759155"
                className="inline-flex items-center justify-center px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors"
              >
                Call Us Now
              </a>
              <a 
                href="https://wa.me/919892759155?text=Hi, I would like to discuss a financing requirement"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
